/**
 * A portal's theme, as the calculator wears it (D4) — on calc.tonnie.ai, and
 * in the preview Tonnie's InOut operation edits it with: the token variables of
 * @tonnie/design-tokens, overridden on <html> — InOut's action colour (the
 * `tone-cyan` family the calculator's actions use), the platform's primary
 * (radios, checkboxes, focus), the page and its text (with the soft fills —
 * muted, accent, secondary — mixed from them, so a hover never shows another
 * page's grey), the radius — and the typeface. A portal's look is worn by its
 * calculator's whole document, and by the firm's storefront on the
 * advisory-firm tab, scoped to that block. What a theme cannot touch, it does not: the colours of risk
 * (amber, red) and "Powered by Tonnie".
 *
 * The action colour is also the colour of small text (the step, a link): when
 * it does not read on the page at 4.5:1, that text takes the page's own text
 * colour instead — the brand on buttons, never unreadable words. The focus
 * ring likewise: the brand when it is seen on the page at 3:1, else the text.
 *
 * The answer's band (white words on InOut's deep gradient) wears the action
 * colour too, deepened in OKLCH — the lightness alone — until white reads on
 * it at 4.5:1.
 *
 * A hovered action moves one shade away from its words — lighter under dark
 * words (Ei!'s gold), deeper under white ones — so its contrast only grows.
 *
 * The typeface is served by Tonnie (`portal-fonts`), never by a font host.
 */
import { contrastRatio, GRAPHIC_CONTRAST, READABLE_CONTRAST } from '@tonnie/schemas';
import type { InOutPortalTheme } from '@tonnie/types';

import { shiftLightness } from './oklch';
import { loadPortalTypeface, type InOutPortalTypeface } from './portal-fonts';

/** Tonnie's page when the portal keeps it — what the action colour's text is read against. */
const DEFAULT_PAGE = '#ffffff';

/** The answer band's words. */
const BAND_TEXT = '#ffffff';

/** How far a hovered action moves from its colour, in OKLCH lightness. */
const HOVER_SHIFT = 0.06;

/** How much deeper the band's gradient starts than the band itself, in OKLCH lightness. */
const BAND_FROM_SHIFT = 0.08;

/** One step of the band's deepening, in OKLCH lightness. */
const BAND_STEP = 0.01;

/** The action colour hovered: a shade away from its words, so they read at least as well. */
export function hoverOf(accent: string, ink: string): string {
  const inkIsDark = contrastRatio(ink, '#ffffff') > contrastRatio(ink, '#000000');
  return shiftLightness(accent, inkIsDark ? HOVER_SHIFT : -HOVER_SHIFT);
}

/**
 * The action colour as deep as white words need it — itself when they already
 * read; deepened in OKLCH, so it stays the brand's hue (Ei!'s gold a darker
 * gold, never olive-brown).
 */
export function bandOf(accent: string): string {
  if (contrastRatio(accent, BAND_TEXT) >= READABLE_CONTRAST) return accent;
  for (let step = 1; step * BAND_STEP <= 1; step++) {
    const shade = shiftLightness(accent, -step * BAND_STEP);
    if (contrastRatio(shade, BAND_TEXT) >= READABLE_CONTRAST) return shade;
  }
  return '#000000';
}

const RADIUS: Record<InOutPortalTheme['radius'], string> = {
  none: '0rem',
  sm: '0.375rem',
  md: '0.5rem',
  lg: '0.75rem',
  xl: '1rem',
};

/**
 * Square corners are square everywhere: the token scale is proportional to
 * `--radius` (design-tokens), and the pills — chips, bars, round buttons — are
 * `rounded-pill`, which reads `--radius-pill` (design-tokens: a pill).
 */
const SQUARE_PILL = { '--radius-pill': '0rem' };

export interface PortalLook {
  variables: Record<string, string>;
  /** The typeface and its CSS family — null for the system's. */
  font: { face: InOutPortalTypeface; family: string } | null;
}

export function portalLookOf(theme: InOutPortalTheme): PortalLook {
  const page = theme.background ?? DEFAULT_PAGE;
  const text =
    contrastRatio(theme.accent, page) >= READABLE_CONTRAST
      ? theme.accent
      : (theme.foreground ?? '#1f2933');
  const band = bandOf(theme.accent);
  // The focus ring must be seen on the page (3:1): the brand when it is, else the text.
  const ring =
    contrastRatio(theme.accent, page) >= GRAPHIC_CONTRAST
      ? theme.accent
      : (theme.foreground ?? '#1f2933');
  const variables: Record<string, string> = {
    '--banner-inout-from': shiftLightness(band, -BAND_FROM_SHIFT),
    '--banner-inout-via': band,
    '--banner-inout-to': band,
    '--tone-cyan-solid': theme.accent,
    '--tone-cyan-on-solid': theme.accentForeground,
    '--tone-cyan-solid-hover': hoverOf(theme.accent, theme.accentForeground),
    '--tone-cyan': text,
    '--tone-cyan-strong': text,
    '--tone-cyan-subtle': `color-mix(in srgb, ${theme.accent} 10%, ${page})`,
    '--tone-cyan-line': `color-mix(in srgb, ${theme.accent} 35%, ${page})`,
    '--primary': theme.accent,
    '--primary-foreground': theme.accentForeground,
    '--ring': ring,
    '--radius': RADIUS[theme.radius],
    ...(theme.radius === 'none' ? SQUARE_PILL : {}),
  };
  if (theme.background && theme.foreground) {
    Object.assign(variables, {
      '--background': theme.background,
      '--card': theme.background,
      '--popover': theme.background,
      '--foreground': theme.foreground,
      '--card-foreground': theme.foreground,
      '--popover-foreground': theme.foreground,
      '--muted': `color-mix(in srgb, ${theme.foreground} 5%, ${theme.background})`,
      '--muted-foreground': `color-mix(in srgb, ${theme.foreground} 70%, ${theme.background})`,
      '--accent': `color-mix(in srgb, ${theme.foreground} 6%, ${theme.background})`,
      '--accent-foreground': theme.foreground,
      '--secondary': `color-mix(in srgb, ${theme.foreground} 6%, ${theme.background})`,
      '--secondary-foreground': theme.foreground,
      '--border': `color-mix(in srgb, ${theme.foreground} 14%, ${theme.background})`,
      '--input': `color-mix(in srgb, ${theme.foreground} 20%, ${theme.background})`,
    });
  }
  const font =
    theme.font === 'system'
      ? null
      : { face: theme.font, family: `'${theme.font}', ui-sans-serif, system-ui, sans-serif` };
  return { variables, font };
}

/** Wears the look on this document — the calculator's page. */
export function wearPortalLook(doc: Document, look: PortalLook): void {
  for (const [name, value] of Object.entries(look.variables)) {
    doc.documentElement.style.setProperty(name, value);
  }
  if (look.font) {
    void loadPortalTypeface(look.font.face);
    doc.body.style.fontFamily = look.font.family;
  }
}
